import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  procurementConfigApi,
  type CreateRegulatoryClauseDto,
  type UpdateRegulatoryClauseDto,
} from '../../../api/procurementConfig';

const brandingKey = ['procurement-config', 'branding'] as const;
const clausesKey = (includeInactive: boolean) =>
  ['regulatory-clauses', includeInactive] as const;

export function useBrandingMetadata() {
  return useQuery({
    queryKey: brandingKey,
    queryFn: () => procurementConfigApi.getBranding(),
    retry: false,
  });
}

export function useBrandingHeaderImage(enabled: boolean) {
  return useQuery({
    queryKey: [...brandingKey, 'header-image'],
    queryFn: () => procurementConfigApi.getHeaderImage(),
    enabled,
  });
}

export function useBrandingFooterImage(enabled: boolean) {
  return useQuery({
    queryKey: [...brandingKey, 'footer-image'],
    queryFn: () => procurementConfigApi.getFooterImage(),
    enabled,
  });
}

export function useUploadBrandingHeader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => procurementConfigApi.uploadHeader(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandingKey });
      qc.invalidateQueries({ queryKey: [...brandingKey, 'header-image'] });
    },
  });
}

export function useUploadBrandingFooter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => procurementConfigApi.uploadFooter(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandingKey });
      qc.invalidateQueries({ queryKey: [...brandingKey, 'footer-image'] });
    },
  });
}

export function useRegulatoryClauses(includeInactive = false) {
  return useQuery({
    queryKey: clausesKey(includeInactive),
    queryFn: () => procurementConfigApi.listClauses(includeInactive),
    retry: false,
  });
}

export function useCreateRegulatoryClause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRegulatoryClauseDto) => procurementConfigApi.createClause(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-clauses'] });
    },
  });
}

export function useUpdateRegulatoryClause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRegulatoryClauseDto }) =>
      procurementConfigApi.updateClause(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-clauses'] });
    },
  });
}

export function useDeleteRegulatoryClause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => procurementConfigApi.deleteClause(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['regulatory-clauses'] });
    },
  });
}
